import { useCallback, useEffect, useState } from 'react';
import { getBillingAccess } from '../utils/accountApi';

/**
 * Billing-access gate for the Console workspace.
 *
 * Owns the { loading, access, error } state, the initial blocking check, and
 * the silent 30-second re-poll. Extracted from App.js unchanged (refactor
 * Step 1); the behavior contract is:
 *
 *  - Superusers and users without an org are treated as active_paid and never
 *    checked against the server.
 *  - The FIRST check blocks the workspace (loading: true until access is
 *    known). Later polls run silently so an active customer is not flashed
 *    behind a loading wall every thirty seconds.
 *  - Polling starts only once auth has resolved (authLoading false) and is
 *    torn down on unmount or when the user changes.
 *
 * @param {{ authLoading: boolean, user: object|null }} params — from useAuth()
 * @returns {{ billingGate: { loading: boolean, access: object|null, error: string },
 *             refreshBillingGate: () => Promise<void> }}
 */
export default function useBillingGate({ authLoading, user }) {
  const [billingGate, setBillingGate] = useState({ loading: true, access: null, error: '' });

  const refreshBillingGate = useCallback(async () => {
    if (!user?.org_id || user?.role === 'superuser') {
      setBillingGate({ loading: false, access: { access_status: 'active_paid' }, error: '' });
      return;
    }
    // The first check blocks the workspace until access is known. Later polls
    // run silently so an active customer is not flashed behind a loading wall
    // every thirty seconds.
    setBillingGate(prev => ({ ...prev, loading: !prev.access, error: '' }));
    try {
      const access = await getBillingAccess();
      setBillingGate({ loading: false, access, error: '' });
    } catch (err) {
      setBillingGate({ loading: false, access: null, error: err.message || 'Account access could not be verified' });
    }
  }, [user?.org_id, user?.role]);

  useEffect(() => {
    if (authLoading) return undefined;
    refreshBillingGate();
    const timer = window.setInterval(refreshBillingGate, 30000);
    return () => window.clearInterval(timer);
  }, [authLoading, refreshBillingGate]);

  return { billingGate, refreshBillingGate };
}
